import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    RefreshCw, Activity, Search, ArrowRight,
    AlertTriangle, AlertCircle, Database, Maximize2,
    Download, Filter, Settings, CheckCircle2, XCircle, Clock, ChevronRight, BarChart2
} from 'lucide-react';
import './DMSDashboard.css';
import CustomDateRangePicker from './CustomDateRangePicker';
import { API_BASE_URL } from '../../config';
import awsLogo from '../../assets/logos/aws-logo.svg';
import gcpLogo from '../../images/gcp-logo.png';
import azureLogo from '../../assets/logos/azure-logo.svg';
import onPremiseLogo from '../../assets/logos/on-premises-logo.svg';
import MigrationDetailsModal from './MigrationDetailsModal';

/* ══════════════════════════════════════════════════════════════
   ENGINE / CLOUD LOGO HELPER
   Returns themed SVG icons based on the engine type string.
   Only renders a logo if the engine name is recognized.
   ══════════════════════════════════════════════════════════════ */
const EngineLogo = ({ name = '', size = 14 }) => {
    const n = name.toUpperCase();

    // MySQL / MariaDB
    if (n.includes('MYSQL') || n.includes('MARIADB')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(245,158,11,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" /><path d="M3 12a9 3 0 0018 0" />
        </svg>
    );
    // PostgreSQL
    if (n.includes('POSTGRES') || n.includes('POSTGRE') || n.includes('PG ') || n === 'PG') return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(59,130,246,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" /><path d="M3 12a9 3 0 0018 0" />
        </svg>
    );
    // MongoDB
    if (n.includes('MONGO')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#22c55e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(34,197,94,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" /><path d="M3 12a9 3 0 0018 0" />
        </svg>
    );
    // Oracle
    if (n.includes('ORACLE')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(239,68,68,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" /><path d="M3 12a9 3 0 0018 0" />
        </svg>
    );
    // SQL Server / MSSQL
    if (n.includes('MSSQL') || n.includes('SQL SERVER') || n.includes('SQLSERVER')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#a855f7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(168,85,247,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" /><path d="M3 12a9 3 0 0018 0" />
        </svg>
    );
    // Redshift (AWS)
    if (n.includes('REDSHIFT')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#ec4899" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(236,72,153,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" /><path d="M3 12a9 3 0 0018 0" />
        </svg>
    );
    // AWS cloud (generic)
    if (n.includes('AWS') || n.includes('AMAZON') || n.includes('RDS') || n.includes('AURORA')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#f97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 16c0 2 1.5 3.5 5 4l6-.5C18.5 19 20 17.5 20 16c0-1.2-.8-2.2-2-2.5.3-.6.5-1.3.5-2 0-2.8-2.5-5-6-5S7 9.2 7 12c0 .7.2 1.4.5 2C6 14.3 4 15 4 16z" fill="rgba(249,115,22,0.1)" />
        </svg>
    );
    // GCP / Google Cloud
    if (n.includes('GCP') || n.includes('GOOGLE') || n.includes('CLOUD SQL') || n.includes('BIGQUERY') || n.includes('SPANNER')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#4285f4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" fill="rgba(66,133,244,0.1)" /><path d="M8 12h8M12 8v8" />
        </svg>
    );
    // Azure / Microsoft
    if (n.includes('AZURE') || n.includes('MICROSOFT') || n.includes('COSMOS')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#0078d4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="4,20 12,4 20,20" fill="rgba(0,120,212,0.1)" /><line x1="8" y1="14" x2="16" y2="14" />
        </svg>
    );
    // DynamoDB
    if (n.includes('DYNAMO')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#f97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(249,115,22,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" />
        </svg>
    );
    // Cassandra
    if (n.includes('CASSANDRA')) return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="eng-logo" fill="none" stroke="#1db9a4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="6" rx="9" ry="3" fill="rgba(29,185,164,0.12)" /><path d="M3 6v12a9 3 0 0018 0V6" /><path d="M3 12a9 3 0 0018 0" />
        </svg>
    );
    // Generic DB fallback
    return null;
};

/* ══════════════════════════════════════════════════════════
   CLOUD PROVIDER LOGO HELPER
   ══════════════════════════════════════════════════════════ */
export const ProviderLogo = ({ name = '', size = 20, className = '' }) => {
    const p = name.toLowerCase();
    if (p.includes('aws') || p.includes('amazon')) return <img src={awsLogo} alt="AWS" width={Math.round(size * 1.25)} height={size} className={`${className} aws-logo-theme-fix`} style={{ objectFit: 'contain', verticalAlign: 'middle', maxHeight: size }} />;
    if (p.includes('gcp') || p.includes('google')) return <img src={gcpLogo} alt="GCP" width={size} height={size} className={className} style={{ objectFit: 'contain', verticalAlign: 'middle', maxHeight: size }} />;
    if (p.includes('azure') || p.includes('microsoft')) return <img src={azureLogo} alt="Azure" width={size} height={size} className={className} style={{ objectFit: 'contain', verticalAlign: 'middle', maxHeight: size }} />;
    return <img src={onPremiseLogo} alt="On-Premise" width={size} height={size} className={className} style={{ objectFit: 'contain', verticalAlign: 'middle', maxHeight: size }} />;
};

const getProviderDisplayName = (name = '') => {
    const p = name.toLowerCase();
    if (p.includes('aws') || p.includes('amazon')) return 'AWS';
    if (p.includes('gcp') || p.includes('google')) return 'GCP';
    if (p.includes('azure') || p.includes('microsoft')) return 'Azure';
    return 'On-Premise';
};

/* byte → human readable */
export const fmtBytes = (b) => {
    if (!b || b <= 0) return null;
    if (b < 1024) return b + ' B';
    if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
    if (b < 1073741824) return (b / 1048576).toFixed(1) + ' MB';
    if (b < 1099511627776) return (b / 1073741824).toFixed(2) + ' GB';
    return (b / 1099511627776).toFixed(2) + ' TB';
};



/* ══════════════════════════════════════════════════════════
   COUNT UP BYTES COMPONENT
   ══════════════════════════════════════════════════════════ */
const CountUpBytes = ({ value }) => {
    const [displayVal, setDisplayVal] = useState(0);
    const prevValueRef = useRef(0);

    useEffect(() => {
        const start = prevValueRef.current;
        const end = value;

        if (start === end) {
            setDisplayVal(end);
            return;
        }

        const duration = 1200; // ms
        const startTime = performance.now();
        let animationFrameId;

        const update = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function (easeOutQuad)
            const easeProgress = progress * (2 - progress);

            const current = Math.floor(start + (end - start) * easeProgress);
            setDisplayVal(current);
            prevValueRef.current = current;

            if (progress < 1) {
                animationFrameId = requestAnimationFrame(update);
            } else {
                setDisplayVal(end);
                prevValueRef.current = end;
            }
        };

        animationFrameId = requestAnimationFrame(update);
        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [value]);

    return <span>{fmtBytes(displayVal) || '0 B'}</span>;
};

/* ══════════════════════════════════════════════════════════
   COUNT UP NUMBER COMPONENT
   ══════════════════════════════════════════════════════════ */
const CountUpNum = ({ value, decimals = 0, suffix = '' }) => {
    const [displayVal, setDisplayVal] = useState(0);
    const prevValueRef = useRef(0);

    useEffect(() => {
        const start = prevValueRef.current;
        const end = value;

        if (start === end) {
            setDisplayVal(end);
            return;
        }

        const duration = 1200; // ms
        const startTime = performance.now();
        let animationFrameId;

        const update = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function (easeOutQuad)
            const easeProgress = progress * (2 - progress);

            const current = start + (end - start) * easeProgress;
            setDisplayVal(current);
            prevValueRef.current = current;

            if (progress < 1) {
                animationFrameId = requestAnimationFrame(update);
            } else {
                setDisplayVal(end);
                prevValueRef.current = end;
            }
        };

        animationFrameId = requestAnimationFrame(update);
        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [value]);

    return <span>{displayVal.toFixed(decimals)}{suffix}</span>;
};

/* ══════════════════════════════════════════════════════════
   RADIAL GAUGE  (Section 4 — Success Rate)
   ══════════════════════════════════════════════════════════ */
const RadialGauge = ({ rate, completed, failed, total }) => {
    const pct = Math.min(100, Math.max(0, rate));
    const remainingPct = 100 - pct;
    const remainingCount = Math.max(0, total - completed - failed);
    const color = pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
    const remainingColor = '#3b82f6';
    const r = 36, cx = 60, cy = 48;
    const circ = Math.PI * r; // half-circle circumference = 113.1
    const strokeDashoffset = circ * (1 - pct / 100);

    const [animatedOffset, setAnimatedOffset] = useState(circ);

    useEffect(() => {
        const t = setTimeout(() => {
            setAnimatedOffset(strokeDashoffset);
        }, 150);
        return () => clearTimeout(t);
    }, [strokeDashoffset, circ]);

    const gradId = `gaugeGrad-${Math.round(rate)}`;

    return (
        <div className="gauge-container-rel">
            {/* Left Section: Gauge + % */}
            <div className="gauge-left">
                <svg viewBox="0 0 120 62" width="100%" className="gauge-ring-svg" style={{ height: '150px', width: 'auto' }}>
                    <defs>
                        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#6366f1" /> {/* Indigo */}
                            <stop offset="100%" stopColor={color} /> {/* Dynamic status color */}
                        </linearGradient>
                    </defs>
                    {/* Track */}
                    <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                        fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" strokeLinecap="round" />
                    {/* Remaining arc */}
                    {pct < 100 && (
                        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                            fill="none" stroke={remainingColor} strokeWidth="8" strokeLinecap="round"
                            strokeDasharray={`0 ${circ * (pct / 100)} ${circ * (1 - pct / 100)} ${circ}`}
                            style={{
                                filter: `drop-shadow(0 0 5px ${remainingColor}30)`,
                                transition: 'stroke-dasharray 1.1s cubic-bezier(0.16,1,0.3,1), stroke 0.3s'
                            }} />
                    )}
                    {/* Filled arc */}
                    {pct > 0 && (
                        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                            fill="none" stroke={`url(#${gradId})`} strokeWidth="8" strokeLinecap="round"
                            strokeDasharray={circ}
                            strokeDashoffset={animatedOffset}
                            style={{
                                filter: `drop-shadow(0 0 6px ${color}50)`,
                                transition: 'stroke-dashoffset 1.4s cubic-bezier(0.16, 1, 0.3, 1), stroke 0.3s'
                            }} />
                    )}
                </svg>
                <div className="gauge-center" style={{ position: 'absolute', top: '72%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span className="gauge-pct-large" style={{ color, fontSize: '2.5rem', fontWeight: 950, lineHeight: 0.95 }}>
                        <CountUpNum value={pct} decimals={1} suffix="%" />
                    </span>
                    <span className="gauge-lbl-sub" style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--d-text-muted)', letterSpacing: '0.06em', marginTop: '4px' }}>Success Rate</span>
                </div>
            </div>

            {/* Right Section: Vertically Stacked Metrics */}
            <div className="gauge-right">
                <div className="gauge-mini-chip" style={{ animationDelay: '0.05s', borderLeft: '3px solid #10b981' }}>
                    <span className="g-chip-label">Completed</span>
                    <span className="g-chip-value" style={{ color: '#10b981' }}>{completed}</span>
                </div>
                <div className="gauge-mini-chip" style={{ animationDelay: '0.12s', borderLeft: '3px solid #ef4444' }}>
                    <span className="g-chip-label">Failed</span>
                    <span className="g-chip-value" style={{ color: '#f87171' }}>{failed}</span>
                </div>
                <div className="gauge-mini-chip" style={{ animationDelay: '0.19s', borderLeft: '3px solid #3b82f6' }}>
                    <span className="g-chip-label">Remaining</span>
                    <span className="g-chip-value" style={{ color: remainingColor }}>{remainingCount}</span>
                </div>
                <div className="gauge-mini-chip" style={{ animationDelay: '0.26s', borderLeft: '3px solid var(--d-text-muted)' }}>
                    <span className="g-chip-label">Total Jobs</span>
                    <span className="g-chip-value" style={{ color: 'var(--d-text-primary)' }}>{total}</span>
                </div>
            </div>

        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════ */
const DMSDashboard = ({ jobs = [], connections = [], onRefreshJobs, onRefreshConnections }) => {

    /* ── State ──────────────────────────────────────────────── */
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [autoRefresh] = useState(true);
    const [activeKpi, setActiveKpi] = useState('total-jobs');
    const [selectedJob, setSelectedJob] = useState(null);
    const [dataGroupBy, setDataGroupBy] = useState('job'); // 'job' | 'source' | 'target'
    const [isTotalDataExpanded, setIsTotalDataExpanded] = useState(false);

    /* ── Filters ────────────────────────────────────────────── */
    const [dateRange, setDateRange] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('All');
    const [targetFilter, setTargetFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });
    const [prevDateRange, setPrevDateRange] = useState('all');

    /* ── Auto-refresh ───────────────────────────────────────── */
    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(() => {
            onRefreshJobs?.();
            onRefreshConnections?.();
        }, 6000);
        return () => clearInterval(id);
    }, [autoRefresh, onRefreshJobs, onRefreshConnections]);


    /* ══════════════════════════════════════════════════════════
       DATA PROCESSING  — purely from props (jobs + connections)
       ══════════════════════════════════════════════════════════ */
    const isDraft = (job) => {
        const sl = (job.status || '').toLowerCase();
        if (sl === 'draft') return true;
        try {
            const opts = typeof job.options === 'string' ? JSON.parse(job.options) : (job.options || {});
            return opts.is_draft === true || opts.isDraft === true;
        } catch { return false; }
    };

    const resolveEngine = (connId, connObj) => {
        if (connObj?.type) return connObj.type.toUpperCase();
        const c = connections.find(x => String(x.id) === String(connId));
        return (c?.type || 'Unknown').toUpperCase();
    };

    const resolveProvider = (connId, connObj) => {
        let provider = connObj?.cloud_provider;
        if (!provider) {
            const c = connections.find(x => String(x.id) === String(connId));
            provider = c?.cloud_provider;

            // Fallback checking database_engine name
            if (!provider && c?.database_engine) {
                const de = c.database_engine.toLowerCase();
                if (de.includes('amazon') || de.includes('rds') || de.includes('aurora')) {
                    provider = 'aws';
                } else if (de.includes('cloud sql') || de.includes('google') || de.includes('gcp')) {
                    provider = 'gcp';
                } else if (de.includes('azure')) {
                    provider = 'azure';
                }
            }
        }
        return provider || 'on-premise';
    };

    const allJobs = useMemo(() => jobs
        .filter(j => !isDraft(j))          // ← EXCLUDE DRAFTS everywhere
        .map(job => {
            const sl = (job.status || '').toLowerCase();
            let status = 'Pending';
            if (['completed', 'success'].includes(sl)) status = 'Completed';
            else if (['failed', 'error'].includes(sl)) status = 'Failed';
            else if (['running', 'syncing', 'cdc_syncing'].includes(sl)) status = 'Running';
            else if (['cancelled', 'stopped', 'suspended'].includes(sl)) status = 'Cancelled';

            const source = resolveEngine(job.source_connection_id, job.source_connection);
            const target = resolveEngine(job.target_connection_id, job.target_connection);
            const sourceProvider = resolveProvider(job.source_connection_id, job.source_connection);
            const targetProvider = resolveProvider(job.target_connection_id, job.target_connection);
            const bytes = parseInt(job.total_bytes) || 0;

            const createdAt = new Date(job.created_at || job.started_at || 0).getTime();
            const startedAt = job.started_at ? new Date(job.started_at) : null;
            const completedAt = job.completed_at ? new Date(job.completed_at) : null;
            const durationMs = startedAt ? Math.max(0, (completedAt || new Date()) - startedAt) : 0;
            const durationStr = durationMs > 0
                ? (() => { const h = Math.floor(durationMs / 3600000), m = Math.floor((durationMs % 3600000) / 60000); return h > 0 ? `${h}h ${m}m` : `${m}m`; })()
                : '—';

            return {
                id: String(job.id),
                name: job.name || `Pipeline [${job.id}]`,
                source, target,
                sourceProvider, targetProvider,
                status, bytes,
                dataSize: fmtBytes(bytes) || '—',
                progress: job.progress ?? null,
                job_type: job.job_type || null,
                options: job.options || {},
                createdAt,
                startTime: startedAt ? startedAt.toLocaleString() : new Date(job.created_at || 0).toLocaleString(),
                endTime: completedAt ? completedAt.toLocaleString() : status === 'Running' ? 'Active' : '—',
                duration: durationStr,
            };
        }), [jobs, connections]); // eslint-disable-line

    /* Unique filter options from actual data or fixed provider list */
    const uniqueSources = ['AWS', 'GCP', 'Azure'];
    const uniqueTargets = ['AWS', 'GCP', 'Azure'];

    /* Base filter */
    const filteredJobs = useMemo(() => allJobs.filter(j => {
        if (sourceFilter !== 'All' && getProviderDisplayName(j.sourceProvider) !== sourceFilter) return false;
        if (targetFilter !== 'All' && getProviderDisplayName(j.targetProvider) !== targetFilter) return false;
        if (statusFilter !== 'All' && j.status !== statusFilter) return false;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            if (!j.name.toLowerCase().includes(q) && !j.source.toLowerCase().includes(q) && !j.target.toLowerCase().includes(q)) return false;
        }
        if (dateRange === '24h') return j.createdAt > Date.now() - 86400000;
        if (dateRange === '7d') return j.createdAt > Date.now() - 604800000;
        if (dateRange === '30d') return j.createdAt > Date.now() - 2592000000;
        if (dateRange === 'custom' && customDateRange.start && customDateRange.end) {
            const startT = new Date(customDateRange.start).getTime();
            const endT = new Date(customDateRange.end).getTime();
            return j.createdAt >= startT && j.createdAt <= endT;
        }
        return true;
    }), [allJobs, sourceFilter, targetFilter, statusFilter, searchQuery, dateRange, customDateRange]);

    /* KPI secondary filter */
    const kpiJobs = useMemo(() => ({
        'total-jobs': filteredJobs,
        running: filteredJobs.filter(j => j.status === 'Running'),
        completed: filteredJobs.filter(j => j.status === 'Completed'),
        failed: filteredJobs.filter(j => j.status === 'Failed'),
        cancelled: filteredJobs.filter(j => j.status === 'Cancelled'),
        pending: filteredJobs.filter(j => j.status === 'Pending'),
        'success-rate': filteredJobs.filter(j => ['Completed', 'Failed'].includes(j.status)),
        'total-data': filteredJobs.filter(j => j.bytes > 0),
    })[activeKpi] ?? filteredJobs, [filteredJobs, activeKpi]);

    /* KPI values (always from base filteredJobs) */
    const total = filteredJobs.length;
    const running = filteredJobs.filter(j => j.status === 'Running').length;

    const hasActiveFilters = dateRange !== 'all' || sourceFilter !== 'All' || targetFilter !== 'All' || statusFilter !== 'All' || searchQuery;

    const handleRefresh = async () => {
        setIsRefreshing(true);
        setDateRange('all');
        setCustomDateRange({ start: null, end: null });
        setShowCustomPicker(false);
        await onRefreshJobs?.();
        await onRefreshConnections?.();
        setTimeout(() => setIsRefreshing(false), 900);
    };

    const resetFilters = () => {
        setDateRange('all'); setSourceFilter('All'); setTargetFilter('All');
        setStatusFilter('All'); setSearchQuery(''); setActiveKpi('total-jobs');
        setSelectedJob(null);
        setCustomDateRange({ start: null, end: null });
        setShowCustomPicker(false);
    };

    const handleRelationshipClick = (src, tgt) => {
        setSourceFilter(src);
        setTargetFilter(tgt);
    };

    /* ══════════════════════════════════════════════════════════
       SECTION 1 — JOB STATUS DONUT CHART
       ══════════════════════════════════════════════════════════ */
    const renderDonut = () => {
        const pendingCount = filteredJobs.filter(j => !['Completed', 'Running', 'Failed', 'Cancelled'].includes(j.status)).length;
        const slices = [
            { label: 'Completed', count: filteredJobs.filter(j => j.status === 'Completed').length, color: '#10b981', kpi: 'completed' },
            { label: 'Running', count: filteredJobs.filter(j => j.status === 'Running').length, color: '#3b82f6', kpi: 'running' },
            { label: 'Failed', count: filteredJobs.filter(j => j.status === 'Failed').length, color: '#ef4444', kpi: 'failed' },
            { label: 'Cancelled', count: filteredJobs.filter(j => j.status === 'Cancelled').length, color: '#f59e0b', kpi: 'cancelled' },
            { label: 'Pending', count: pendingCount, color: '#94a3b8', kpi: 'pending' },
        ];
        const tot = filteredJobs.length;
        if (tot === 0) return <EmptyState label="No Data Available" />;

        const R = 34, C = 2 * Math.PI * R;
        let acc = 0;
        const segs = slices.filter(s => s.count > 0).map(s => {
            const pct = (s.count / tot) * 100;
            const gap = 2.2;
            const len = Math.max(0, (pct * C) / 100 - gap);
            const offset = acc === 0 ? 0 : C - (acc * C) / 100;
            acc += pct;
            return { ...s, pct, dash: `${len.toFixed(2)} ${(C - len).toFixed(2)}`, offset: offset.toFixed(2) };
        });

        const allKpis = ['running', 'completed', 'failed', 'cancelled', 'pending'];

        return (
            <div className="donut-wrap">
                <div className="donut-svg-box">
                    <svg viewBox="0 0 100 100" width="100%" height="100%">
                        {/* Outer background glow */}
                        <circle cx="50" cy="50" r={R + 10} fill="none" stroke="rgba(255,255,255,0.02)" strokeWidth="1" />
                        {/* Inner track */}
                        <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="16" />
                        {segs.map((s, i) => {
                            const isSelected = activeKpi === s.kpi;
                            const isAnySelected = allKpis.includes(activeKpi);
                            const opacity = !isAnySelected || isSelected ? 1 : 0.35;
                            return (
                                <circle key={i} cx="50" cy="50" r={R} fill="none"
                                    stroke={s.color} strokeWidth={isSelected ? 18 : 16}
                                    strokeDasharray={s.dash} strokeDashoffset={s.offset}
                                    transform="rotate(-90 50 50)"
                                    className="donut-seg"
                                    style={{
                                        cursor: 'pointer',
                                        opacity,
                                        filter: isSelected ? `drop-shadow(0 0 5px ${s.color}cc)` : 'none',
                                        transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)'
                                    }}
                                    onClick={() => setActiveKpi(activeKpi === s.kpi ? 'total-jobs' : s.kpi)}>
                                    <title>{s.label}: {s.count} ({s.pct.toFixed(1)}%)</title>
                                </circle>
                            );
                        })}
                        {/* Clickable center — resets to show all jobs */}
                        <g
                            onClick={() => setActiveKpi('total-jobs')}
                            style={{ cursor: 'pointer' }}
                        >
                            <circle cx="50" cy="50" r="22" fill="transparent" />
                            <text x="50" y="46" textAnchor="middle" fill="var(--d-text-primary)" fontSize="15" fontWeight="950">{tot}</text>
                            <text x="50" y="58" textAnchor="middle" fill="var(--d-text-muted)" fontSize="6.5" fontWeight="800" style={{ letterSpacing: '0.04em', textTransform: 'uppercase' }}>Total Jobs</text>
                        </g>
                    </svg>
                </div>
                <div className="donut-legend">
                    {slices.map(s => {
                        const pct = tot > 0 ? ((s.count / tot) * 100).toFixed(1) : '0.0';
                        const isSelected = activeKpi === s.kpi;
                        const isAnySelected = allKpis.includes(activeKpi);
                        const opacity = !isAnySelected || isSelected ? 1 : 0.4;
                        return (
                            <div key={s.label} className={`donut-legend-row ${isSelected ? 'active' : ''}`}
                                onClick={() => s.count > 0 && setActiveKpi(activeKpi === s.kpi ? 'total-jobs' : s.kpi)}
                                style={{ cursor: s.count > 0 ? 'pointer' : 'default', opacity, transition: 'all 0.2s' }}>
                                <span className="legend-dot" style={{ background: s.color, boxShadow: `0 0 5px ${s.color}80` }} />
                                <span className="legend-name">{s.label}</span>
                                <span className="legend-count">{s.count}</span>
                                <span className="legend-pct">{pct}%</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    /* ══════════════════════════════════════════════════════════
       SECTION 2 — TOTAL DATA MIGRATED (centered hero design)
       ══════════════════════════════════════════════════════════ */
    const renderTotalDataMigrated = () => {
        const totalBytes = kpiJobs.reduce((acc, j) => acc + (j.bytes || 0), 0);
        const completedBytes = kpiJobs
            .filter(j => j.status === 'Completed')
            .reduce((acc, j) => acc + (j.bytes || 0), 0);
        const totalJobsCount = kpiJobs.length;
        const completedJobsCount = kpiJobs.filter(j => j.status === 'Completed').length;

        if (totalBytes === 0) {
            return <EmptyState label="No Migrated Data Available" />;
        }

        return (
            <div className="tdm-center-layout">
                {/* Decorative glow behind the hero number */}
                <div className="tdm-glow-bg" />

                {/* Hero number with count-up animation */}
                <div className="tdm-hero-value">
                    <CountUpBytes value={completedBytes} />
                </div>
                <div className="tdm-hero-label">Total Data Migrated</div>

                {/* Two stat chips */}
                <div className="tdm-chips-row">
                    <div className="tdm-chip">
                        <span className="tdm-chip-label">Total Volume</span>
                        <span className="tdm-chip-val">{fmtBytes(totalBytes)}</span>
                    </div>
                    <div className="tdm-chip-sep" />
                    <div className="tdm-chip">
                        <span className="tdm-chip-label">Jobs Completed</span>
                        <span className="tdm-chip-val" style={{ color: '#10b981' }}>
                            {completedJobsCount} of {totalJobsCount}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderTotalDataExpandedModal = () => {
        const aggregated = {};
        kpiJobs.forEach(j => {
            if (j.bytes <= 0) return;
            let key = '';
            let label = '';
            let iconName = '';
            if (dataGroupBy === 'job') {
                key = j.id;
                label = j.name;
                iconName = j.source;
            } else if (dataGroupBy === 'source') {
                key = j.source;
                label = j.source;
                iconName = j.source;
            } else if (dataGroupBy === 'target') {
                key = j.target;
                label = j.target;
                iconName = j.target;
            }

            if (!aggregated[key]) {
                aggregated[key] = { label, bytes: 0, count: 0, iconName };
            }
            aggregated[key].bytes += j.bytes;
            aggregated[key].count++;
        });

        const items = Object.values(aggregated).sort((a, b) => b.bytes - a.bytes);
        const maxB = items.length > 0 ? items[0].bytes : 0;
        const colors = ['#38bdf8', '#818cf8', '#10b981', '#f59e0b', '#ef4444', '#c084fc', '#06b6d4', '#34d399', '#fbbf24', '#60a5fa'];

        return (
            <div className="total-data-modal-overlay" onClick={() => setIsTotalDataExpanded(false)}>
                <div className="total-data-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="total-data-modal-header">
                        <div className="total-data-modal-title-group">
                            <h3 className="total-data-modal-title">Detailed Migration Volume</h3>
                            <span className="total-data-modal-subtitle">Breakdown of migrated data by selected dimension</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                            <div className="d-card-tabs">
                                <button className={`d-card-tab ${dataGroupBy === 'job' ? 'active' : ''}`} onClick={() => setDataGroupBy('job')}>Migration Job</button>
                                <button className={`d-card-tab ${dataGroupBy === 'source' ? 'active' : ''}`} onClick={() => setDataGroupBy('source')}>Source DB</button>
                                <button className={`d-card-tab ${dataGroupBy === 'target' ? 'active' : ''}`} onClick={() => setDataGroupBy('target')}>Target DB</button>
                            </div>
                            <button className="total-data-modal-close" onClick={() => setIsTotalDataExpanded(false)}>×</button>
                        </div>
                    </div>

                    <div className="total-data-modal-body">
                        {items.length === 0 ? (
                            <EmptyState label="No Migration Data Available" />
                        ) : (
                            <div className="hbar-list">
                                {items.map((item, i) => {
                                    const pct = maxB > 0 ? (item.bytes / maxB) * 100 : 0;
                                    const col = colors[i % colors.length];
                                    return (
                                        <div key={item.label} className="hbar-row" style={{ padding: '0.4rem 0' }}>
                                            <div className="hbar-label-row">
                                                <span className="hbar-path-name">
                                                    {item.iconName && <EngineLogo name={item.iconName} size={14} />}
                                                    <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>{item.label}</span>
                                                </span>
                                                <span className="hbar-val" style={{ color: col, fontSize: '0.85rem' }}>{fmtBytes(item.bytes)}</span>
                                            </div>
                                            <div className="hbar-track" style={{ height: '10px' }}>
                                                <div className="hbar-fill progress-animated-glow" style={{ width: `${Math.max(pct, 2)}%`, background: `linear-gradient(90deg, ${col}99, ${col})`, boxShadow: `0 0 8px ${col}30` }} />
                                            </div>
                                            <div className="hbar-sub" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.2rem', fontSize: '0.68rem', color: 'var(--d-text-muted)' }}>
                                                <span>{item.count} migration{item.count !== 1 ? 's' : ''}</span>
                                                <span>{pct.toFixed(1)}% of maximum</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    /* ══════════════════════════════════════════════════════════
       SECTION 3 — SOURCE → TARGET MIGRATION RELATIONSHIP
       ══════════════════════════════════════════════════════════ */
    const renderRelationship = () => {
        const map = {};
        const total = allJobs.length;

        const defaults = [
            ['aws', 'gcp'],
            ['aws', 'azure'],
            ['gcp', 'aws'],
            ['gcp', 'azure'],
            ['azure', 'gcp'],
            ['azure', 'aws']
        ];

        defaults.forEach(([s, t]) => {
            const srcDisplay = getProviderDisplayName(s);
            const tgtDisplay = getProviderDisplayName(t);
            const k = `${srcDisplay}|||${tgtDisplay}`;
            map[k] = { src: s, tgt: t, srcDisplay, tgtDisplay, count: 0, completed: 0, failed: 0 };
        });

        allJobs.forEach(j => {
            const sp = j.sourceProvider || 'unknown';
            const tp = j.targetProvider || 'unknown';
            if (sp === 'unknown' || tp === 'unknown') return;

            const srcDisplay = getProviderDisplayName(sp);
            const tgtDisplay = getProviderDisplayName(tp);

            const k = `${srcDisplay}|||${tgtDisplay}`;
            if (!map[k]) {
                map[k] = { src: sp, tgt: tp, srcDisplay, tgtDisplay, count: 0, completed: 0, failed: 0 };
            }
            map[k].count++;
            if (j.status === 'Completed') map[k].completed++;
            if (j.status === 'Failed') map[k].failed++;
        });

        const predefinedOrder = [
            'AWS|||GCP',
            'AWS|||Azure',
            'GCP|||AWS',
            'GCP|||Azure',
            'Azure|||GCP',
            'Azure|||AWS'
        ];

        const items = Object.values(map).sort((a, b) => {
            const idxA = predefinedOrder.indexOf(`${a.srcDisplay}|||${a.tgtDisplay}`);
            const idxB = predefinedOrder.indexOf(`${b.srcDisplay}|||${b.tgtDisplay}`);

            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;

            return b.count - a.count;
        });

        if (!items.length) return <EmptyState label="No Migration Relationships Available" />;

        const maxCount = items.length > 0 ? Math.max(...items.map(i => i.count)) : 0;
        const colors = ['#818cf8', '#38bdf8', '#a78bfa', '#34d399', '#fbbf24'];

        return (
            <div className="rel-list" style={{
                height: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                alignContent: 'start',
                rowGap: '1rem',
                columnGap: '1.5rem',
                padding: '0.6rem 0'
            }}>
                {items.map((item, i) => {
                    const pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    const successPct = item.count > 0 ? Math.round((item.completed / item.count) * 100) : 0;
                    const barColor = colors[i % colors.length];
                    const spColor = successPct >= 80 ? '#10b981' : successPct >= 50 ? '#f59e0b' : '#ef4444';
                    const pctOfTotal = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0';
                    return (
                        <div
                            key={`${item.srcDisplay}-${item.tgtDisplay}`}
                            className="rel-row"
                            style={{ cursor: 'pointer', animationDelay: `${i * 0.08}s` }}
                            onClick={() => handleRelationshipClick(item.srcDisplay, item.tgtDisplay)}
                        >
                            <div className="rel-label-row">
                                <span className="rel-path" style={{ display: 'flex', alignItems: 'center' }}>
                                    <ProviderLogo name={item.src} size={24} />
                                    <span className="rel-arrow" style={{ margin: '0 12px', color: 'var(--d-text-muted)' }}>➔</span>
                                    <ProviderLogo name={item.tgt} size={24} />
                                </span>
                            </div>
                            <div className="rel-bar-container">
                                <div className="rel-track">
                                    <div className="rel-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${barColor}aa, ${barColor})`, boxShadow: `0 0 6px ${barColor}30` }} />
                                </div>
                                <span className="rel-count-end">{item.count}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    /* ══════════════════════════════════════════════════════════
       SECTION 5 — ACTIVE MIGRATION MONITOR
       ══════════════════════════════════════════════════════════ */
    const renderMonitor = () => {
        const active = kpiJobs.filter(j => j.status === 'Running');
        if (!active.length) return <EmptyState label="No Active Migrations" icon="live" />;
        return (
            <div className="d-table-scroll">
                <table className="d-table" style={{ tableLayout: 'auto', width: '100%', whiteSpace: 'nowrap' }}>
                    <thead><tr>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Job Name</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Source</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Target</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Progress</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Started</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Status</th>
                    </tr></thead>
                    <tbody>
                        {active.map(job => (
                            <tr key={job.id} className="d-table-row" onClick={() => setSelectedJob(job)} style={{ height: '48px' }}>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <div className="d-name-cell" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', maxWidth: '100%', overflow: 'hidden', width: '100%' }}>
                                        <span className="d-job-name" title={job.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.name}</span>
                                        <span className="d-live-badge" style={{ flexShrink: 0 }}>LIVE</span>
                                    </div>
                                </td>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ letterSpacing: '0.01em', color: 'var(--d-text-primary)' }}>{getProviderDisplayName(job.sourceProvider)}</span></td>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ letterSpacing: '0.01em', color: 'var(--d-text-primary)' }}>{getProviderDisplayName(job.targetProvider)}</span></td>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {job.progress !== null ? (
                                        <div className="d-prog-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%' }}>
                                            <div className="d-prog-track" style={{ flexGrow: 1, maxWidth: '100px' }}><div className="d-prog-fill" style={{ width: `${job.progress}%` }} /></div>
                                            <span className="d-prog-num" style={{ flexShrink: 0 }}>{job.progress}%</span>
                                        </div>
                                    ) : <span className="d-muted d-small">Syncing…</span>}
                                </td>
                                <td className="d-muted" style={{ textAlign: 'center', padding: '10px 16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.startTime}</td>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><div style={{ display: 'flex', justifyContent: 'center' }}><span className="d-badge d-badge-running">Running</span></div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    /* ══════════════════════════════════════════════════════════
       SECTION 6 — RECENT MIGRATION ACTIVITIES
       ══════════════════════════════════════════════════════════ */
    const renderActivities = () => {
        const sorted = [...kpiJobs].sort((a, b) => b.createdAt - a.createdAt);
        if (!sorted.length) return <EmptyState label="No Recent Activities Available" icon="activity" />;
        const badgeCls = s => ({ Running: 'running', Completed: 'completed', Failed: 'failed', Cancelled: 'cancelled', Pending: 'pending' }[s] || '');
        return (
            <div className="d-table-scroll">
                <table className="d-table" style={{ tableLayout: 'auto', width: '100%', whiteSpace: 'nowrap' }}>
                    <thead><tr>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Job Name</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Source</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Target</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Status</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Data Size</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Start Time</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>End Time</th>
                        <th style={{ textAlign: 'center', padding: '12px 16px' }}>Duration</th>
                    </tr></thead>
                    <tbody>
                        {sorted.map(job => (
                            <tr key={job.id} className="d-table-row" onClick={() => setSelectedJob(job)} style={{ height: '48px' }}>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <div className="d-name-cell" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', maxWidth: '100%', overflow: 'hidden', width: '100%' }}>
                                        <span className="d-job-name" title={job.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.name}</span>
                                        {job.status === 'Running' && <span className="d-live-badge" style={{ flexShrink: 0 }}>LIVE</span>}
                                    </div>
                                </td>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ letterSpacing: '0.01em', color: 'var(--d-text-primary)' }}>{getProviderDisplayName(job.sourceProvider)}</span></td>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><span style={{ letterSpacing: '0.01em', color: 'var(--d-text-primary)' }}>{getProviderDisplayName(job.targetProvider)}</span></td>
                                <td style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><div style={{ display: 'flex', justifyContent: 'center' }}><span className={`d-badge d-badge-${badgeCls(job.status)}`}>{job.status}</span></div></td>
                                <td className="d-bytes-cell" style={{ textAlign: 'center', padding: '10px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.bytes > 0 ? job.dataSize : '—'}</td>
                                <td className="d-muted" style={{ textAlign: 'center', padding: '10px 16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.startTime}</td>
                                <td className="d-muted" style={{ textAlign: 'center', padding: '10px 16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.endTime}</td>
                                <td className="d-muted" style={{ textAlign: 'center', padding: '10px 16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.duration}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };




    /* ══════════════════════════════════════════════════════════
       RENDER
       ══════════════════════════════════════════════════════════ */
    return (
        <div className="dboard dms-page-container dms-connections-container">

            {/* ── Header ── */}
            <div className="dms-page-header">
                <div className="header-title-stack">
                    <h2>Dashboard</h2>
                    <p className="header-subtitle">Real-time operational overview, key metrics, and migration status analytics.</p>
                </div>
                <div className="dboard-header-right">
                    <button className={`d-btn d-btn-icon ${isRefreshing ? 'spin-border' : ''}`} onClick={handleRefresh} title="Refresh Data">
                        <RefreshCw size={13} className={isRefreshing ? 'spin' : ''} />
                    </button>
                </div>
            </div>

            {/* ── Filters Panel ── */}
            <div className="d-filters-panel anim-slide-down">
                <div className="d-filters-row">
                    <div className="d-filter-item" style={{ position: 'relative' }}>
                        <label>Timeframe</label>
                        <select value={dateRange} onChange={e => {
                            if (e.target.value === 'custom') {
                                setPrevDateRange(dateRange);
                                setShowCustomPicker(true);
                            } else {
                                setShowCustomPicker(false);
                                setCustomDateRange({ start: null, end: null });
                            }
                            setDateRange(e.target.value);
                        }}>
                            <option value="all">All Time</option>
                            <option value="24h">Last 24 h</option>
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="custom">
                                {dateRange === 'custom' && customDateRange.start
                                    ? `${new Date(customDateRange.start).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })} - ${new Date(customDateRange.end).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}`
                                    : 'Custom...'}
                            </option>
                        </select>
                        {showCustomPicker && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: '0.5rem' }}>
                                <CustomDateRangePicker
                                    initialStart={customDateRange.start}
                                    initialEnd={customDateRange.end}
                                    onApply={(start, end) => {
                                        setCustomDateRange({ start, end });
                                        setShowCustomPicker(false);
                                        setDateRange('custom');
                                    }}
                                    onCancel={() => {
                                        setShowCustomPicker(false);
                                        if (!customDateRange.start) setDateRange(prevDateRange);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div className="d-filter-item">
                        <label>Source Provider</label>
                        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
                            <option value="All">All Sources</option>
                            {uniqueSources.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="d-filter-item">
                        <label>Target Provider</label>
                        <select value={targetFilter} onChange={e => setTargetFilter(e.target.value)}>
                            <option value="All">All Targets</option>
                            {uniqueTargets.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div className="d-filter-item">
                        <label>Status</label>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="All">All Status</option>
                            <option value="Running">Running</option>
                            <option value="Completed">Completed</option>
                            <option value="Failed">Failed</option>
                            <option value="Canceled">Canceled</option>
                        </select>
                    </div>
                    <div className="d-filter-item">
                        <label style={{ visibility: 'hidden' }}>Search</label>
                        <div className="d-search-wrapper">
                            <Search size={14} className="d-search-icon-new" />
                            <input type="text" placeholder="Search jobs…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                        </div>
                    </div>
                    {hasActiveFilters && (
                        <button className="d-reset-btn" onClick={resetFilters} style={{ height: '36px' }}>Reset Filters</button>
                    )}
                </div>
            </div>



            {/* ── Row 1: Donut + Total Data Migrated ── */}
            <div className="d-row d-row-2col">
                <DCard title="Job Status Distribution" subtitle="Overview of all migration jobs grouped by their current execution status.">
                    {renderDonut()}
                </DCard>
                <DCard
                    title="Total Data Migrated"
                    subtitle="Displays the total amount of data successfully transferred across all migration activities."
                    trailing={
                        <button
                            className="d-btn d-btn-icon"
                            onClick={() => setIsTotalDataExpanded(true)}
                            title="Expand details"
                            style={{
                                padding: '0.4rem 0.65rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.35rem',
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid var(--d-card-border)'
                            }}
                        >
                            <Maximize2 size={11} style={{ opacity: 0.8 }} />
                            <span style={{ fontSize: '0.7rem', fontWeight: 700 }}>Expand</span>
                        </button>
                    }
                >
                    {renderTotalDataMigrated()}
                </DCard>
            </div>

            {/* ── Row 2: Relationship + Gauge ── */}
            <div className="d-row d-row-2col">
                <DCard title="Source → Target Migration Relationships" subtitle="Shows the connection mapping between source systems and their destination migration targets." className="d-card-compact">
                    {renderRelationship()}
                </DCard>
                <DCard title="Success Rate" subtitle="Percentage of completed migration jobs compared with total initiated jobs." className="d-card-compact">
                    {filteredJobs.length === 0
                        ? <EmptyState label="No Success Data Available" />
                        : <RadialGauge
                            rate={filteredJobs.length > 0 ? (filteredJobs.filter(j => j.status === 'Completed').length / filteredJobs.length) * 100 : 0}
                            completed={filteredJobs.filter(j => j.status === 'Completed').length}
                            failed={filteredJobs.filter(j => j.status === 'Failed').length}
                            total={filteredJobs.length}
                        />
                    }
                </DCard>
            </div>

            {/* ── Active Monitor ── */}
            <DCard title="Active Migration Monitor" subtitle="Provides real-time visibility into currently running migration processes and progress." live={running > 0} liveCount={running}>
                {renderMonitor()}
            </DCard>

            {/* ── Drilldown ── */}
            {selectedJob && <MigrationDetailsModal job={selectedJob} connections={connections} onClose={() => setSelectedJob(null)} />}

            {/* ── Total Data Expanded Modal ── */}
            {isTotalDataExpanded && renderTotalDataExpandedModal()}

            {/* ── Recent Activities ── */}
            <DCard title="Migration Jobs" subtitle="Detailed history of recent migration tasks including status, timing, and execution details." trailing={<a href="#/dms/jobs" className="d-view-all">View All →</a>}>
                {renderActivities()}
            </DCard>

        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
   SHARED CARD WRAPPER
   ══════════════════════════════════════════════════════════════ */
const DCard = ({ title, subtitle, children, live, liveCount, trailing, className = '' }) => (
    <div className={`d-card ${className}`}>
        <div className="d-card-header">
            <div className="d-card-title-group">
                <h4 className="d-card-title">{title}</h4>
                {subtitle && <span className="d-card-sub">{subtitle}</span>}
            </div>
            {live && (
                <span className="d-live-pill">
                    <span className="blink-dot" />{liveCount} running
                </span>
            )}
            {trailing}
        </div>
        <div className="d-card-body">{children}</div>
    </div>
);

/* ══════════════════════════════════════════════════════════════
   EMPTY STATE
   ══════════════════════════════════════════════════════════════ */
const EmptyState = ({ label, icon }) => (
    <div className="d-empty">
        {icon === 'live' && <Activity size={26} style={{ opacity: 0.2 }} />}
        {icon === 'activity' && <AlertTriangle size={26} style={{ opacity: 0.2 }} />}
        {!icon && <Database size={26} style={{ opacity: 0.2 }} />}
        <span>{label}</span>
    </div>
);

export default DMSDashboard;
